import { defineStore } from 'pinia'
import { get, post, restDelete, submitFormData } from './common'
import { api } from '@/composables/api'

interface Backup {
  id?: string
  name: string
  size: number
  date: string
}

export const useBackupStore = defineStore('backup', {
  state: () => ({
    backups: [] as Backup[],
    loading: false,
    error: ''
  }),

  getters: {
    getBackups(state) {
      return state.backups
    },
    isLoading(state) {
      return state.loading
    },
    getError(state) {
      return state.error
    }
  },

  actions: {
    async fetchBackups() {
      this.loading = true
      this.error = ''
      try {
        const response = await get('/backups')
        this.backups = response.data || response || []
      } catch (err: any) {
        console.error('Error loading backups:', err)
        this.error = 'Failed to load backups'
        this.backups = []
      } finally {
        this.loading = false
      }
    },

    async createBackup() {
      try {
        await post('/backups', {})
        await this.fetchBackups()
        return { success: true, message: 'Backup created successfully' }
      } catch (err: any) {
        console.error('Error creating backup:', err)
        const message = err.response?.data?.detail || 'Failed to create backup'
        return { success: false, message }
      }
    },

    async downloadBackup(backup: Backup) {
      try {
        // Strip .gz extension from name for API path
        const backupName = backup.name.replace(/\.gz$/, '')

        // Use api composable for blob response
        const response = await api.download(`/backups/${backupName}/download`)

        return { success: true, message: 'Backup downloaded successfully' }
      } catch (err: any) {
        console.error('Error downloading backup:', err)
        const message = err.response?.data?.detail || 'Failed to download backup'
        return { success: false, message }
      }
    },

    async restoreBackup(backupName: string) {
      try {
        await post(`/backups/${backupName}/restore`, {})
        await this.fetchBackups()
        return { success: true, message: 'Database restored successfully' }
      } catch (err: any) {
        console.error('Error restoring backup:', err)
        const message = err.response?.data?.detail || 'Failed to restore backup'
        return { success: false, message }
      }
    },

    async deleteBackup(backupName: string) {
      try {
        await restDelete(`/backups/${backupName}`)
        await this.fetchBackups()
        return { success: true, message: 'Backup deleted successfully' }
      } catch (err: any) {
        console.error('Error deleting backup:', err)
        const message = err.response?.data?.detail || 'Failed to delete backup'
        return { success: false, message }
      }
    },

    async uploadAndRestore(file: File) {
      try {
        const formData = new FormData()
        formData.append('file', file)

        // Use submitFormData for multipart/form-data
        await api.submitFormData('/backups/restore', formData)

        await this.fetchBackups()
        return { success: true, message: 'Backup restored successfully' }
      } catch (err: any) {
        console.error('Error uploading and restoring backup:', err)
        const message = err.response?.data?.detail || 'Failed to restore from uploaded backup'
        return { success: false, message }
      }
    }
  }
})
